import { useEffect, useState } from "react";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { firestore } from "../shared/FirebaseConfig";

// How many times has each student already been signed out TODAY — so a teacher can
// glance before saying "yes, go again" (e.g. a kid who's asked to leave every
// period). Counts COMPLETED trips from this teacher's own sign-out history
// (`users/{uid}/signedInHistory`); the caller adds +1 for a currently-active
// sign-out. Live (onSnapshot) so the count ticks up the moment a kid is signed
// back in.
//
// Scope: a teacher's history is owner-only by the security rules, so this is "trips
// from MY room today", not a school-wide total. A cross-teacher count needs the
// denormalized school-readable sign-out log (see IDEAS.md "School-wide Student
// Insights") — deliberately out of scope here.
//
// Returns two views of the same counts so either sign-out flow can read it with no
// extra queries:
//   byDocId — keyed by the student's Firestore doc id (the name-grid tile key)
//   byId    — keyed by the student's ID number (what the ID sign-out form types)
export default function useTripsToday(uid) {
  const [trips, setTrips] = useState({ byDocId: {}, byId: {} });

  useEffect(() => {
    if (!uid) {
      setTrips({ byDocId: {}, byId: {} });
      return undefined;
    }
    // Local midnight as an absolute instant. time_out is stored as an ISO string,
    // so a lexicographic >= range on it is chronological (single-field, no index).
    const start = new Date();
    start.setHours(0, 0, 0, 0);

    const q = query(
      collection(firestore, "users", uid, "signedInHistory"),
      where("time_out", ">=", start.toISOString())
    );
    return onSnapshot(
      q,
      (snap) => {
        const byDocId = {};
        const byId = {};
        snap.forEach((d) => {
          const data = d.data();
          const docKey = data.studentDocumentNumber;
          if (docKey) byDocId[docKey] = (byDocId[docKey] || 0) + 1;
          if (data.idNumber != null && data.idNumber !== "") {
            const idKey = String(data.idNumber).trim();
            byId[idKey] = (byId[idKey] || 0) + 1;
          }
        });
        setTrips({ byDocId, byId });
      },
      (err) => console.error("[useTripsToday]", err)
    );
  }, [uid]);

  return trips;
}
